import { IActions } from '@/interfaces/store.interfaces';
import { CopilotServiceApplication } from '@/application/copilot-service-application';
import { ChatUIMessageType } from '@/enums';
import { CopilotUserSessionMessageType, ICopilotUserSession, ICopilotUserSessionDetails } from 'interfaces';
import { UserSession } from '@/interfaces/user-session.interface';
import { RenderIncentiveCommand, Referrer, ActionState } from '@studyportals/authentication-service-interfaces';
import { LoginStatusCheckerPlugin } from '@/plugins/login-status-checker-plugin';
import globals from '@/utils/globals';
import { StudentRepository } from '@/domain/student-repository';
import { StudentClient } from '@/infrastructure/student-client';
import { ProfileDataCheckerPlugin } from '@/plugins/profile-data-checker-plugin';


const actions: IActions = {
	setAreDependenciesLoaded: async (areDependenciesLoaded: boolean) => {
		if (areDependenciesLoaded) {
			await new LoginStatusCheckerPlugin().initiate();

			const studentClient = new StudentClient(globals.anonymousStudentService);
			globals.studentRepository = new StudentRepository(studentClient);

			await new ProfileDataCheckerPlugin().initiate();
		}

		CopilotServiceApplication.Store.mutations.setAreDependenciesLoaded(areDependenciesLoaded);
	},
	addMessageToChatHistory: (value: { role: ChatUIMessageType; text: string }) => {
		const newChatHistory = CopilotServiceApplication.Store.state.chatHistory.slice();
		newChatHistory.push(value);
		CopilotServiceApplication.Store.mutations.changeChatHistory(newChatHistory);
	},
	removeChatHistoryMessageAtIndex: (index: number) => {
		const newChatHistory = CopilotServiceApplication.Store.state.chatHistory.slice();
		newChatHistory.splice(index, 1);
		CopilotServiceApplication.Store.mutations.changeChatHistory(newChatHistory);
	},
	cleanChatHistory: () => {
		CopilotServiceApplication.Store.mutations.changeChatHistory([]);
	},
	enableUserSessionLoading: () => {
		CopilotServiceApplication.Store.mutations.setCanLoadUserSession(true);
	},
	loadUserSessions: (value: ICopilotUserSession[]) => {
		const userSessions: UserSession[] = value.map(({ sessionId, timestamp }) => {
			const milliseconds = timestamp * 1000;
			const date = new Date(milliseconds);
			const day = date.toLocaleString('en-US', { weekday: 'short' });
			const month = date.toLocaleString('en-US', { month: 'long' });
			const dayOfMonth = date.toLocaleString('en-US', { day: 'numeric' });
			const year = date.toLocaleString('en-US', { year: 'numeric' });
			const ampm = date.toLocaleString('en-US', { hour: 'numeric', minute: 'numeric', hour12: true });
			const formattedDate = `${day}, ${month} ${dayOfMonth}, ${year}, ${ampm}`;

			return {
				id: sessionId,
				dateText: formattedDate,
			}
		});

		CopilotServiceApplication.Store.mutations.setUserSessions(userSessions);
	},
	loadUserSession: (value: ICopilotUserSessionDetails) => {
		const messagesToLoadIntoChatHistory: { role: ChatUIMessageType; text: string }[] = value.history.map(({ message, type }) => {
			return {
				role: type === CopilotUserSessionMessageType.USER
					? ChatUIMessageType.USER
					: ChatUIMessageType.AI,
				text: message,
			};
		});

		CopilotServiceApplication.Store.mutations.changeChatHistory(messagesToLoadIntoChatHistory);
		CopilotServiceApplication.Store.mutations.changeChatSessionId(value.sessionId);
	},
	toggleSidebar: () => {
		CopilotServiceApplication.Store.mutations.setSidebarOpen(!CopilotServiceApplication.Store.state.isSidebarOpen);
	},
	toggleFooter: (value: boolean) => {
		CopilotServiceApplication.Store.mutations.setShouldShowFooter(value);
	},
	renderIncentiveCommand: () => {
		const command = new RenderIncentiveCommand(Referrer.COPILOT, ActionState.THIRD_PARTY_REGISTRATION);
		globals.authenticationServiceApplication?.renderIncentive(command);
	},
};

export default actions;
